import { all, call, put, select, takeLatest } from "redux-saga/effects";
import { API } from "../../../api/api";
import { ToasterService } from "../../../services/toaster/toaster.service";
import { HallsActionTypes } from "../actions/actionTypes";
import { hallsAPI } from "../api/api";
import { hallsStoreSelectors } from "../store/store";
import {
    checkAuthorization,
    checkPermission
} from "./../../../utils/Authorization/authorization";
import {
    AuthorizationServiceTypes,
    AuthorizationRouteTypes
} from "../../../utils/Authorization/authorizationTypes";

function* getHallsList(action) {
    try {
        const halls = yield call(() =>
            hallsAPI.getHalls({ ...action.payload })
        );

        const hallIDs = halls.data.results.map(hall => hall.id).join(",");

        let servers = { data: [] };
        let serverIDs = "";
        if (checkPermission("list_servers")) {
            servers = yield* _getServersFromIds(hallIDs);
            serverIDs = servers.data["results"]
                .map(servers => servers.id)
                .join(",");
        }

        let licensesData;
        if (
            checkAuthorization({
                service: AuthorizationServiceTypes.LICENSES
            }) &&
            servers.data["results"] &&
            servers.data["results"].length > 0
        ) {
            const licenses = yield* _getLicensesDataFromIds(serverIDs);
            licensesData = licenses.data;
        } else {
            licensesData = { results: [] };
        }

        yield all({
            hallList: put({
                type: HallsActionTypes.GET_HALLS_LIST_SUCCESS,
                payload: {
                    halls: halls.data,
                    servers: servers.data,
                    licenses: licensesData
                }
            })
        });
    } catch (e) {
        API.handleError(e.response, "Error getting halls list.");

        yield put({
            type: HallsActionTypes.GET_HALLS_LIST_FAIL,
            message: e.message
        });
    }
}

function* applyFilters() {
    try {
        const filters = yield select(hallsStoreSelectors.filters);

        const halls = yield call(() =>
            hallsAPI.getHalls({ page: 0, top: 12, search: null, filters })
        );

        const hallIDs = halls.data.results.map(hall => hall.id).join(",");
        const servers = yield* _getServersFromIds(hallIDs);
        const serverIDs = servers.data.results
            .map(servers => servers.id)
            .join(",");

        let licensesData;
        if (
            checkAuthorization({
                service: AuthorizationServiceTypes.LICENSES
            }) &&
            servers.data.results.length > 0
        ) {
            const licenses = yield* _getLicensesDataFromIds(serverIDs);
            licensesData = licenses.data;
        } else {
            licensesData = { results: [] };
        }

        let status = [];
        if (servers.data.results.length > 0) {
            servers.data["results"].map(s=>
                status.push({
                    id: s.id,
                    latest_config_id: s.latest_config_id,
                    version: s.version
                })
            )
        }

        yield all({
            hallList: put({
                type: HallsActionTypes.GET_HALLS_LIST_SUCCESS,
                payload: {
                    halls: halls.data,
                    servers: servers.data,
                    servers_status: status,
                    licenses: licensesData
                }
            })
        });
    } catch (e) {
        API.handleError(e.response, "Error getting halls list.");

        yield put({
            type: HallsActionTypes.GET_HALLS_LIST_FAIL,
            message: e.message
        });
    }
}

/**
 * Returns the servers that match the provided hallIds.
 * @param hallIDs
 */
function* _getServersFromIds(hallIDs: any) {
    return yield call(() => hallsAPI.getHallListServers({ hallIds: hallIDs }));
}

/**
 * Returns the licenses that match the hallIds provided.
 * @param serverIds
 */
function* _getLicensesDataFromIds(serverIds: any) {
    let licenses = { data: { results: [] } };
    if (serverIds.length > 0) {
        const licensesRequest = yield call(() =>
            hallsAPI.getLicenses({ filter: "server_id in (" + serverIds +",)" })
        );

        return licensesRequest;
    }

    return licenses;
}

function* clearFilters(action) {
    if (action.payload.requestHalls) {
        yield call(applyFilters);
    }
}

function* toggleHallActiveState(action) {
    const hallId = action.payload.hallId;

    try {
        let status = yield call(() => hallsAPI.activateHall(hallId));

        if (status.status == 202) {
            yield put({
                type: HallsActionTypes.TOGGLE_HALL_ACTIVE_SUCCESS,
                payload: {
                    hallId: hallId
                }
            });
        } else {
            ToasterService.errorToast("Error changing hall status.");
        }
    } catch (e) {
        API.handleError(e.response, "Error changing hall status.");

        yield put({
            type: HallsActionTypes.TOGGLE_HALL_ACTIVE_FAIL,
            message: e.message
        });
    }
}

export function* getHallsListWatcher() {
    yield takeLatest(HallsActionTypes.GET_HALLS_LIST, getHallsList);
    yield takeLatest(HallsActionTypes.CLEAR_HALL_LIST_FILTERS, clearFilters);
    yield takeLatest(HallsActionTypes.CLEAR_HALL_LIST_FILTER, applyFilters);
    yield takeLatest(HallsActionTypes.CHANGE_HALL_LIST_FILTER, applyFilters);
    yield takeLatest(
        HallsActionTypes.TOGGLE_HALL_ACTIVE,
        toggleHallActiveState
    );
}
